/**
 * Editor Page
 * Dedicated page for JSON data editing
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Save,
  Plus,
  Trash2,
  Edit3,
  Download,
  Upload,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  User,
  Package,
  Eye,
  EyeOff,
  Home,
  Calendar,
  Network
} from 'lucide-react';
import { validatePersonData, validateItemsData } from '../utils/dataValidation';
import { exportImprovedFormat } from '../utils/jsonFormatMigration';

const EditorPage = () => {
  const [activeTab, setActiveTab] = useState('people');
  const [peopleData, setPeopleData] = useState([]);
  const [itemsData, setItemsData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showRawJSON, setShowRawJSON] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Detect if we're in development mode
  const isDevelopment = import.meta.env?.DEV || process.env.NODE_ENV === 'development';

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [peopleResponse, itemsResponse] = await Promise.all([
        fetch('/people.json'),
        fetch('/items.json')
      ]);

      const people = await peopleResponse.json();
      const items = await itemsResponse.json();

      setPeopleData(Array.isArray(people) ? people : []);
      setItemsData(typeof items === 'object' ? items : {});
      setError(null);
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate data whenever it changes
  useEffect(() => {
    if (activeTab === 'people') {
      const validation = validatePersonData(peopleData);
      setValidationResults(validation);
    } else {
      const validation = validateItemsData(itemsData);
      setValidationResults(validation);
    }
  }, [peopleData, itemsData, activeTab]);

  // Filter data based on search
  const filteredPeopleData = useMemo(() => {
    if (!searchTerm) return peopleData;

    return peopleData.filter(person => {
      const searchText = [
        person.name_jp,
        person.name_romaji,
        person.description,
        person.region,
        ...(person.connections || []),
        ...(person.items || [])
      ].join(' ').toLowerCase();

      return searchText.includes(searchTerm.toLowerCase());
    });
  }, [peopleData, searchTerm]);

  const filteredItemsData = useMemo(() => {
    if (!searchTerm) return itemsData;

    const filtered = {};
    Object.entries(itemsData).forEach(([itemName, connections]) => {
      const searchText = [
        itemName,
        ...connections.map(conn => `${conn.person} ${conn.connection}`)
      ].join(' ').toLowerCase();

      if (searchText.includes(searchTerm.toLowerCase())) {
        filtered[itemName] = connections;
      }
    });

    return filtered;
  }, [itemsData, searchTerm]);

  // Add new person
  const addNewPerson = () => {
    const newPerson = {
      name_jp: "",
      name_romaji: "",
      years: "",
      region: "",
      description: "",
      connections: [],
      items: []
    };

    setPeopleData(prev => [...prev, newPerson]);
    setEditingIndex(peopleData.length);
    setHasUnsavedChanges(true);
  };

  // Add new item
  const addNewItem = () => {
    const itemName = prompt('Enter item name:');
    if (itemName && !itemsData[itemName]) {
      setItemsData(prev => ({
        ...prev,
        [itemName]: []
      }));
      setHasUnsavedChanges(true);
    }
  };

  // Update person
  const updatePerson = (index, field, value) => {
    setPeopleData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setHasUnsavedChanges(true);
  };

  // Delete person
  const deletePerson = (index) => {
    if (confirm('Are you sure you want to delete this person?')) {
      setPeopleData(prev => prev.filter((_, i) => i !== index));
      setHasUnsavedChanges(true);
      setEditingIndex(null);
    }
  };

  // Delete item
  const deleteItem = (itemName) => {
    if (confirm(`Are you sure you want to delete "${itemName}"?`)) {
      setItemsData(prev => {
        const updated = { ...prev };
        delete updated[itemName];
        return updated;
      });
      setHasUnsavedChanges(true);
    }
  };

  // Add connection to item
  const addItemConnection = (itemName) => {
    const person = prompt('Enter person name:');
    const connection = prompt('Enter connection type:');

    if (person && connection) {
      setItemsData(prev => ({
        ...prev,
        [itemName]: [
          ...(prev[itemName] || []),
          { person, connection }
        ]
      }));
      setHasUnsavedChanges(true);
    }
  };

  // Download JSON file
  const downloadJSON = (filename, data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export improved format with numerical IDs
  const exportImprovedJSON = () => {
    try {
      const improvedData = exportImprovedFormat(peopleData, itemsData);

      // Download the new format files
      downloadJSON('people_v2.json', improvedData.people);
      downloadJSON('items_v2.json', improvedData.items);
      downloadJSON('relationships_v2.json', improvedData.relationships);
      downloadJSON('migration_report.json', {
        validation: improvedData.validation,
        migration_info: improvedData.migration_info
      });

      alert(`Improved format exported successfully!\n\nFiles generated:\n- people_v2.json (${improvedData.people.length} people with numerical IDs)\n- items_v2.json (${improvedData.items.length} items with numerical IDs)\n- relationships_v2.json (${improvedData.relationships.length} relationships)\n- migration_report.json\n\n${improvedData.validation.warnings.length > 0 ? 'Warnings: ' + improvedData.validation.warnings.join(', ') : 'No warnings'}`);
    } catch (err) {
      setError(`Failed to export improved format: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            <p>Loading JSON data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100"
            >
              <Home size={16} />
              Home
            </Link>
            <span className="text-gray-300">•</span>
            <h1 className="text-2xl font-semibold text-gray-800">
              Data Editor
              {!isDevelopment && <span className="text-sm font-normal text-orange-600 ml-2">(Read-only)</span>}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/timeline"
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            >
              <Calendar size={16} />
              Timeline View
            </Link>
            <Link
              to="/network"
              className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-100 text-emerald-700 rounded-md hover:bg-emerald-200"
            >
              <Network size={16} />
              Network View
            </Link>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          {isDevelopment
            ? 'Edit people and items data for the tea ceremony visualizer. Changes can be saved and exported.'
            : 'View and export tea ceremony data. Editing is only available in development mode.'
          }
        </p>

        {/* Main Editor Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Tabs */}
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('people')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'people'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <User size={16} />
              People ({peopleData.length})
            </button>
            <button
              onClick={() => setActiveTab('items')}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'items'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Package size={16} />
              Items ({Object.keys(itemsData).length})
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
            <div className="flex items-center gap-3">
              {/* Search */}
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              />

              {/* Add button - only in development */}
              {isDevelopment && (
                <button
                  onClick={activeTab === 'people' ? addNewPerson : addNewItem}
                  className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600"
                >
                  <Plus size={16} />
                  Add {activeTab === 'people' ? 'Person' : 'Item'}
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* View toggle */}
              <button
                onClick={() => setShowRawJSON(!showRawJSON)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                {showRawJSON ? <EyeOff size={16} /> : <Eye size={16} />}
                {showRawJSON ? 'Form View' : 'Raw JSON'}
              </button>

              {/* Export button */}
              <button
                onClick={() => downloadJSON(`${activeTab}.json`, activeTab === 'people' ? peopleData : itemsData)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                <Download size={16} />
                Export
              </button>

              {/* Migration button - only in development */}
              {isDevelopment && (
                <button
                  onClick={exportImprovedJSON}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-md text-sm font-medium hover:bg-purple-600"
                  title="Export improved JSON format with numerical IDs and better relationships"
                >
                  <RefreshCw size={16} />
                  Export v2 Format
                </button>
              )}
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle size={16} />
                <span className="font-medium">Error</span>
              </div>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          )}

          {/* Validation errors */}
          {validationResults && !validationResults.isValid && (
            <div className="mx-4 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2 text-yellow-700 mb-2">
                <AlertCircle size={16} />
                <span className="font-medium">Validation Issues</span>
              </div>
              <ul className="text-sm text-yellow-600 space-y-1">
                {validationResults.errors.slice(0, 5).map((error, index) => (
                  <li key={index}>• {error.message}</li>
                ))}
                {validationResults.errors.length > 5 && (
                  <li>• ... and {validationResults.errors.length - 5} more issues</li>
                )}
              </ul>
            </div>
          )}

          {/* Main content area with proper scrolling */}
          <div className="h-[600px]">
            {showRawJSON ? (
              <div className="h-full p-4">
                <textarea
                  value={JSON.stringify(activeTab === 'people' ? peopleData : itemsData, null, 2)}
                  onChange={(e) => {
                    if (!isDevelopment) return; // Prevent editing in production

                    try {
                      const parsed = JSON.parse(e.target.value);
                      if (activeTab === 'people') {
                        setPeopleData(Array.isArray(parsed) ? parsed : []);
                      } else {
                        setItemsData(typeof parsed === 'object' ? parsed : {});
                      }
                      setHasUnsavedChanges(true);
                      setError(null);
                    } catch (err) {
                      setError(`Invalid JSON: ${err.message}`);
                    }
                  }}
                  className={`w-full h-full p-4 border border-gray-300 rounded-md font-mono text-sm resize-none ${
                    !isDevelopment ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                  spellCheck={false}
                  readOnly={!isDevelopment}
                />
              </div>
            ) : (
              <div className="h-full overflow-y-auto">
                {activeTab === 'people' ? (
                  <PeopleEditor
                    people={filteredPeopleData}
                    onUpdate={updatePerson}
                    onDelete={deletePerson}
                    editingIndex={editingIndex}
                    setEditingIndex={setEditingIndex}
                    isDevelopment={isDevelopment}
                  />
                ) : (
                  <ItemsEditor
                    items={filteredItemsData}
                    onUpdate={setItemsData}
                    onDelete={deleteItem}
                    onAddConnection={addItemConnection}
                    isDevelopment={isDevelopment}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {hasUnsavedChanges && (
                  <span className="text-orange-600 font-medium">Unsaved changes</span>
                )}
              </div>

              {/* Validation status */}
              {validationResults && (
                <div className="flex items-center gap-2">
                  {validationResults.isValid ? (
                    <CheckCircle className="text-green-500" size={16} />
                  ) : (
                    <AlertCircle className="text-red-500" size={16} />
                  )}
                  <span className="text-sm">
                    {validationResults.isValid ? 'Valid' : `${validationResults.errors.length} errors`}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                <RefreshCw size={16} />
                Reload
              </button>

              {/* Save button - only in development */}
              {isDevelopment && hasUnsavedChanges && (
                <button
                  onClick={() => {
                    downloadJSON('people.json', peopleData);
                    downloadJSON('items.json', itemsData);
                    setHasUnsavedChanges(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600"
                >
                  <Save size={16} />
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// People Editor Sub-component
const PeopleEditor = ({ people, onUpdate, onDelete, editingIndex, setEditingIndex, isDevelopment }) => {
  return (
    <div className="p-4 space-y-4">
      {people.map((person, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              {editingIndex === index && isDevelopment ? (
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Japanese name"
                    value={person.name_jp}
                    onChange={(e) => onUpdate(index, 'name_jp', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Romaji name"
                    value={person.name_romaji}
                    onChange={(e) => onUpdate(index, 'name_romaji', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Years (e.g., 1522–1591)"
                    value={person.years}
                    onChange={(e) => onUpdate(index, 'years', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Region"
                    value={person.region}
                    onChange={(e) => onUpdate(index, 'region', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <textarea
                    placeholder="Description"
                    value={person.description}
                    onChange={(e) => onUpdate(index, 'description', e.target.value)}
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded text-sm h-20 resize-none"
                  />
                </div>
              ) : (
                <div>
                  <h3 className="font-semibold text-lg">
                    {person.name_jp} <span className="text-gray-500 font-normal">/ {person.name_romaji}</span>
                  </h3>
                  <p className="text-sm text-gray-600">{person.years} • {person.region}</p>
                  <p className="text-sm mt-2">{person.description}</p>
                  {person.connections?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-gray-700">Connections: </span>
                      <span className="text-xs text-gray-600">{person.connections.join(', ')}</span>
                    </div>
                  )}
                  {person.items?.length > 0 && (
                    <div className="mt-1">
                      <span className="text-xs font-medium text-gray-700">Items: </span>
                      <span className="text-xs text-gray-600">{person.items.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Edit/Delete buttons - only in development */}
            {isDevelopment && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                  className="p-2 text-gray-500 hover:text-blue-600"
                  title="Edit"
                >
                  <Edit3 size={16} />
                </button>
                <button
                  onClick={() => onDelete(index)}
                  className="p-2 text-gray-500 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>

          {editingIndex === index && isDevelopment && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => setEditingIndex(null)}
                className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
              >
                Done Editing
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Items Editor Sub-component
const ItemsEditor = ({ items, onUpdate, onDelete, onAddConnection, isDevelopment }) => {
  return (
    <div className="p-4 space-y-4">
      {Object.entries(items).map(([itemName, connections]) => (
        <div key={itemName} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{itemName}</h3>
              <div className="mt-2 space-y-2">
                {connections.map((conn, connIndex) => (
                  <div key={connIndex} className="flex items-center gap-3 text-sm">
                    <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                    <span className="font-medium">{conn.person}</span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-600">{conn.connection}</span>
                  </div>
                ))}
                {connections.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No connections</p>
                )}
              </div>
            </div>

            {/* Edit/Delete buttons - only in development */}
            {isDevelopment && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => onAddConnection(itemName)}
                  className="p-2 text-gray-500 hover:text-green-600"
                  title="Add connection"
                >
                  <Plus size={16} />
                </button>
                <button
                  onClick={() => onDelete(itemName)}
                  className="p-2 text-gray-500 hover:text-red-600"
                  title="Delete item"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditorPage;